import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Ticket, MessageCircle, LayoutDashboard, CreditCard, Map, MapPin, Inbox, Settings, LogOut, ExternalLink, Menu, X, Bell } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import Overview from "@/pages/admin/Overview";
import BookingsAdmin from "@/pages/admin/BookingsAdmin";
import ToursAdmin from "@/pages/admin/ToursAdmin";
import DestinationsAdmin from "@/pages/admin/DestinationsAdmin";
import InboxAdmin from "@/pages/admin/InboxAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";
import ReviewsAdmin from "@/pages/admin/ReviewsAdmin";
import CouponsAdmin from "@/pages/admin/CouponsAdmin";

const TABS = [
  { k: "overview", label: "Resumen", Icon: LayoutDashboard },
  { k: "bookings", label: "Pagos y Reservas", Icon: CreditCard },
  { k: "tours", label: "Excursiones", Icon: Map },
  { k: "destinations", label: "Destinos", Icon: MapPin },
  { k: "coupons", label: "Cupones", Icon: Ticket },
  { k: "reviews", label: "Comentarios", Icon: MessageCircle },
  { k: "inbox", label: "Notificaciones", Icon: Inbox },
  { k: "settings", label: "Diseño y Ajustes", Icon: Settings },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { settings, refresh } = useSite();
  const [tab, setTab] = useState("overview");
  const [ready, setReady] = useState(false);
  const [stats, setStats] = useState(null);
  const [mobileNav, setMobileNav] = useState(false);

  const lastAlert = React.useRef(undefined);
  const beep = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      [0, 0.18, 0.36].forEach((t, i) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = [880, 1175, 1568][i];
        g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
        g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.16);
        o.connect(g); g.connect(ctx.destination); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.18);
      });
    } catch { /* ignore */ }
  };
  const loadStats = useCallback(() => api.get("/admin/stats").then((r) => {
    setStats(r.data);
    const la = r.data.latest_alert;
    if (lastAlert.current !== undefined && la && la.id !== lastAlert.current) {
      const label = { compra: "Nueva venta", contacto: "Nuevo mensaje", comentario: "Nuevo comentario" }[la.kind] || "Nueva notificación";
      beep();
      toast.success(`${label}: ${la.subject}`, { duration: 10000 });
      if ("Notification" in window && Notification.permission === "granted") {
        try { new Notification(`RD Explora · ${label}`, { body: la.subject, icon: "/favicon.ico" }); } catch { /* ignore */ }
      }
    }
    lastAlert.current = la ? la.id : null;
  }).catch(() => {}), []);

  // Live alerts: poll every 20s while the panel is open
  useEffect(() => {
    if (!ready) return;
    const t = setInterval(loadStats, 20000);
    return () => clearInterval(t);
  }, [ready, loadStats]);
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const askNotif = async () => {
    if (typeof Notification === "undefined") return;
    const p = await Notification.requestPermission(); setNotifPerm(p);
    if (p === "granted") { beep(); toast.success("Alertas del navegador activadas"); }
  };

  useEffect(() => {
    if (!localStorage.getItem("rdx_admin_token")) { navigate("/admin/login"); return; }
    api.get("/auth/me").then(() => { setReady(true); loadStats(); }).catch(() => {
      localStorage.removeItem("rdx_admin_token"); navigate("/admin/login");
    });
  }, [navigate, loadStats]);

  const logout = () => { localStorage.removeItem("rdx_admin_token"); navigate("/admin/login"); };
  const onChange = () => { loadStats(); refresh(); };

  if (!ready) return null;
  const unread = (stats?.messages_unread || 0) + (stats?.notifications_unread || 0);

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="admin-dashboard">
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-64 shrink-0 bg-white border-r border-gray-200 flex flex-col transition-transform duration-300 ${mobileNav ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 border-b border-gray-100 flex items-center gap-3">
          {settings?.logo_url && <img src={mediaUrl(settings.logo_url)} alt="" className="h-10 w-auto" />}
          <div><div className="font-bold text-gray-900 leading-tight">{settings?.site_name}</div><div className="text-xs text-gray-500">Administración</div></div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {TABS.map(({ k, label, Icon }) => (
            <button key={k} onClick={() => { setTab(k); setMobileNav(false); }} data-testid={`admin-tab-${k}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === k ? "brand-bg text-white" : "text-gray-600 hover:bg-gray-100"}`}>
              <Icon className="w-4 h-4" />{label}
              {k === "inbox" && unread > 0 && <span className={`ml-auto text-[11px] px-1.5 rounded-full ${tab === k ? "bg-white/25" : "accent-bg text-[var(--accent-fg)]"}`}>{unread}</span>}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-gray-100 space-y-1">
          <Link to="/" target="_blank" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100"><ExternalLink className="w-4 h-4" />Ver sitio</Link>
          <button onClick={logout} data-testid="admin-logout-btn" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-600 hover:bg-red-50"><LogOut className="w-4 h-4" />Cerrar sesión</button>
        </div>
      </aside>
      {mobileNav && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setMobileNav(false)} />}
      <div className="flex-1 min-w-0">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMobileNav(!mobileNav)} aria-label="Menú">{mobileNav ? <X /> : <Menu />}</button>
            <h1 className="text-lg font-semibold text-gray-900">{TABS.find((x) => x.k === tab)?.label}</h1>
          </div>
          <div className="flex items-center gap-2">
          {notifPerm === "default" && (
            <button onClick={askNotif} className="hidden sm:flex text-xs font-medium brand-text border border-[var(--brand)] rounded-full px-3 py-1.5 hover:bg-[var(--brand)]/5" data-testid="enable-browser-alerts-btn">Activar alertas con sonido</button>
          )}
          <button onClick={() => setTab("inbox")} className="relative p-2 text-gray-600 hover:text-[var(--brand)]" aria-label="Notificaciones" data-testid="admin-bell-btn">
            <Bell className="w-5 h-5" />
            {unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full accent-bg" />}
          </button>
          </div>
        </header>
        <main className="p-4 sm:p-8">
          {tab === "overview" && <Overview stats={stats} goTo={setTab} />}
          {tab === "bookings" && <BookingsAdmin onChange={onChange} />}
          {tab === "tours" && <ToursAdmin onChange={onChange} />}
          {tab === "destinations" && <DestinationsAdmin onChange={onChange} />}
          {tab === "coupons" && <CouponsAdmin />}
          {tab === "reviews" && <ReviewsAdmin onChange={onChange} />}
          {tab === "inbox" && <InboxAdmin onChange={onChange} />}
          {tab === "settings" && <SettingsAdmin onChange={onChange} />}
        </main>
      </div>
    </div>
  );
}
